import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

import { api, ApiError, type MascotChatTurn } from "../auth/client";
import type { CourseNarration } from "../courses/useCourseRunner";
import { renderMarkdown } from "../mascot/markdown";
import {
  hasSeenTutorial,
  markTutorialSeen,
  TUTORIAL_STEPS,
} from "../mascot/tutorial";

import { MascotCanvas } from "./MascotCanvas";

const SIZE_KEY = "realchem.mascot.size.v1";
const POS_KEY = "realchem.mascot.pos.v1";
const HIDDEN_KEY = "realchem.mascot.hidden.v1";
const MIN_SIZE = 90;
const MAX_SIZE = 260;
const DEFAULT_SIZE = 150;
// Pixels the pointer must travel before we treat the gesture as a
// drag rather than a click. Below the threshold the pointerup falls
// through to the button's onClick (toggle move-mode / hide etc).
const DRAG_THRESHOLD_PX = 4;

type Pos = { x: number; y: number };

function loadSize(): number {
  try {
    const raw = window.localStorage.getItem(SIZE_KEY);
    if (raw) {
      const n = Number(raw);
      if (Number.isFinite(n)) return Math.min(MAX_SIZE, Math.max(MIN_SIZE, n));
    }
  } catch {
    // localStorage unavailable; fall through to default.
  }
  return DEFAULT_SIZE;
}

function loadPos(): Pos | null {
  try {
    const raw = window.localStorage.getItem(POS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Pos;
    if (
      typeof parsed.x === "number"
      && typeof parsed.y === "number"
      && Number.isFinite(parsed.x)
      && Number.isFinite(parsed.y)
    ) {
      return parsed;
    }
  } catch {
    // Bad JSON or no localStorage — drop back to CSS defaults.
  }
  return null;
}

function loadHidden(): boolean {
  try {
    return window.localStorage.getItem(HIDDEN_KEY) === "1";
  } catch {
    return false;
  }
}

type Props = {
  /** Force the tutorial to start from step 0 even if the user has
   *  already seen it. The Toolbar's "Tour" button uses this. */
  forceOpen?: boolean;
  onCloseTour?: () => void;
  /** When a Course is mid-narration the runner pushes a bubble
   *  payload here; the mascot renders it as a narrate-panel (third
   *  bubble kind) and the user advances by clicking Continue. */
  narration?: CourseNarration | null;
};

type Panel = "tutorial" | "chat" | "narrate" | null;

export function Mascot({ forceOpen, onCloseTour, narration }: Props) {
  const [stepIndex, setStepIndex] = useState(0);
  const [panel, setPanel] = useState<Panel>(() =>
    hasSeenTutorial() ? null : "tutorial",
  );
  const [assetMissing, setAssetMissing] = useState(false);

  const [size, setSize] = useState<number>(loadSize);
  const [pos, setPos] = useState<Pos | null>(loadPos);
  const [moveMode, setMoveMode] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [hidden, setHidden] = useState<boolean>(loadHidden);

  // Refs for drag math. The island ref is the whole positioned
  // container — drag offsets are computed from its bounding rect so
  // the figure never drifts relative to the cursor regardless of
  // which element initiated the gesture.
  const islandRef = useRef<HTMLDivElement | null>(null);
  type DragState = {
    pointerId: number;
    startX: number;
    startY: number;
    islandLeft: number;
    islandTop: number;
    dragging: boolean;
    target: HTMLElement;
  };
  const dragRef = useRef<DragState | null>(null);

  // Chat state
  const [chatHistory, setChatHistory] = useState<MascotChatTurn[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const chatScrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (forceOpen) {
      setStepIndex(0);
      setPanel("tutorial");
    }
  }, [forceOpen]);

  // Course narration trumps everything else — when the runner is
  // active we force the bubble into narrate mode; when the runner
  // releases the narration (sets it back to null) we drop the
  // bubble unless the user has another panel open.
  useEffect(() => {
    if (narration) {
      setPanel("narrate");
    } else {
      setPanel((p) => (p === "narrate" ? null : p));
    }
  }, [narration]);

  // Persist size + position + hidden state when they change.
  useEffect(() => {
    try {
      window.localStorage.setItem(SIZE_KEY, String(size));
    } catch {
      // ignore
    }
  }, [size]);
  useEffect(() => {
    if (!pos) return;
    try {
      window.localStorage.setItem(POS_KEY, JSON.stringify(pos));
    } catch {
      // ignore
    }
  }, [pos]);
  useEffect(() => {
    try {
      window.localStorage.setItem(HIDDEN_KEY, hidden ? "1" : "0");
    } catch {
      // ignore
    }
  }, [hidden]);

  // Auto-scroll the chat to the bottom as new messages arrive.
  useEffect(() => {
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chatHistory, chatBusy]);

  const step = TUTORIAL_STEPS[stepIndex];
  const isLastStep = stepIndex === TUTORIAL_STEPS.length - 1;

  const closeTour = useCallback(() => {
    setPanel((p) => (p === "tutorial" ? null : p));
    markTutorialSeen();
    onCloseTour?.();
  }, [onCloseTour]);

  const nextStep = () => {
    if (isLastStep) {
      closeTour();
    } else {
      setStepIndex((i) => i + 1);
    }
  };
  const backStep = () => setStepIndex((i) => Math.max(0, i - 1));

  // Animation hint priority: course narration wins (it carries its
  // own pose hint), then tutorial step, then chat-busy → talk, else
  // idle.
  const animation
    = panel === "narrate" && narration
      ? narration.animation ?? "talk"
      : panel === "tutorial"
        ? step.animation
        : chatBusy
          ? "talk"
          : "idle";

  // ---- Drag handling ----
  // Threshold-based: pointerdown records the start; pointermove only
  // promotes to a real drag once the cursor has moved more than a few
  // pixels. Below the threshold, pointerup falls through and the
  // browser's synthetic click fires normally — that's what makes the
  // move button "click to toggle, press-and-drag to move" work
  // without two competing handlers.
  const beginDrag = (e: ReactPointerEvent<HTMLElement>) => {
    const island = islandRef.current;
    if (!island) return;
    const rect = island.getBoundingClientRect();
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      islandLeft: rect.left,
      islandTop: rect.top,
      dragging: false,
      target: e.currentTarget,
    };
  };
  const updateDrag = (e: ReactPointerEvent<HTMLElement>) => {
    const s = dragRef.current;
    if (!s) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (!s.dragging) {
      if (Math.hypot(dx, dy) <= DRAG_THRESHOLD_PX) return;
      s.dragging = true;
      s.target.setPointerCapture(s.pointerId);
    }
    const island = islandRef.current;
    const w = island?.offsetWidth ?? size;
    const h = island?.offsetHeight ?? size;
    const newX = s.islandLeft + dx;
    const newY = s.islandTop + dy;
    setPos({
      x: Math.max(8, Math.min(window.innerWidth - w - 8, newX)),
      y: Math.max(8, Math.min(window.innerHeight - h - 8, newY)),
    });
  };
  /** Returns true if the gesture was a real drag (so the caller can
   *  suppress the synthetic click that would otherwise toggle the
   *  associated mode). */
  const endDrag = (_e: ReactPointerEvent<HTMLElement>): boolean => {
    const s = dragRef.current;
    if (!s) return false;
    const wasDragging = s.dragging;
    if (wasDragging) {
      s.target.releasePointerCapture(s.pointerId);
    }
    dragRef.current = null;
    return wasDragging;
  };

  // Figure: only draggable when the user has explicitly turned move
  // mode on (existing behavior — protects against accidental drags
  // while interacting with the canvas behind the mascot).
  const onFigurePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!moveMode) return;
    beginDrag(e);
  };
  const onFigurePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!moveMode) return;
    updateDrag(e);
  };
  const onFigurePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!moveMode) return;
    endDrag(e);
  };

  // Move button: drag handle ALWAYS, regardless of mode. A short
  // tap (no drag) falls through to onClick and toggles move mode for
  // the figure.
  const onMoveButtonPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    beginDrag(e);
  };
  const onMoveButtonPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    updateDrag(e);
  };
  const onMoveButtonPointerUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const wasDrag = endDrag(e);
    if (wasDrag) {
      // Suppress the synthetic click that would otherwise fire
      // immediately after pointerup with capture released.
      e.preventDefault();
    }
  };

  const onLoaded = useCallback(() => setAssetMissing(false), []);
  const onError = useCallback(() => setAssetMissing(true), []);

  // Send a chat message. We append the user turn locally first, fire
  // the request, then append the reply on success or surface a
  // friendly error on failure.
  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatBusy) return;
    const next: MascotChatTurn[] = [
      ...chatHistory,
      { role: "user", content: text },
    ];
    setChatHistory(next);
    setChatInput("");
    setChatBusy(true);
    setChatError(null);
    try {
      const { reply } = await api.mascotChat(next);
      setChatHistory((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (err) {
      const message
        = err instanceof ApiError
          ? err.code === "no_token"
            ? "Sign in to chat with Axie — the AI tools live behind auth."
            : err.code === "ai_unavailable"
              ? "AI is offline right now (no API key configured)."
              : `Chat failed (${err.code}).`
          : "Chat failed (network error).";
      setChatError(message);
    } finally {
      setChatBusy(false);
    }
  };

  const onChatKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void sendChat();
    }
  };

  // The whole mascot island is positioned absolutely. CSS handles
  // the default corner, but once the user drags it we switch to
  // explicit top/left coordinates from state.
  const islandStyle: React.CSSProperties = pos
    ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" }
    : {};

  const figureStyle: React.CSSProperties = { width: size, height: size };

  return (
    <div
      ref={islandRef}
      className={
        "mascot"
        + (moveMode ? " is-move-mode" : "")
        + (hidden ? " is-hidden" : "")
      }
      data-tour-open={panel === "tutorial" ? "true" : "false"}
      style={islandStyle}
    >
      {panel === "narrate" && narration && !hidden && (
        <div
          className="mascot-bubble mascot-bubble-narrate"
          role="dialog"
          aria-label={`Course narration: ${narration.courseTitle}`}
        >
          <div className="mascot-narrate-header">
            <strong className="mascot-narrate-course">
              {narration.courseTitle}
            </strong>
            <span className="mascot-narrate-progress">{narration.progress}</span>
          </div>
          <p className="mascot-bubble-text">{narration.says}</p>
          <div className="mascot-bubble-actions">
            <div className="mascot-bubble-buttons">
              <button
                type="button"
                className="mascot-btn mascot-btn-ghost"
                onClick={narration.onCancel}
              >
                Exit course
              </button>
              <button
                type="button"
                className="mascot-btn"
                onClick={narration.onContinue}
                autoFocus
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {panel === "tutorial" && !hidden && (
        <div className="mascot-bubble" role="dialog" aria-label="Tutorial">
          <p className="mascot-bubble-text">{step.text}</p>
          <div className="mascot-bubble-actions">
            <span className="mascot-bubble-progress">
              {stepIndex + 1} / {TUTORIAL_STEPS.length}
            </span>
            <div className="mascot-bubble-buttons">
              <button
                type="button"
                className="mascot-btn mascot-btn-ghost"
                onClick={closeTour}
              >
                Skip
              </button>
              <button
                type="button"
                className="mascot-btn mascot-btn-ghost"
                onClick={backStep}
                disabled={stepIndex === 0}
              >
                Back
              </button>
              <button type="button" className="mascot-btn" onClick={nextStep}>
                {isLastStep ? "Done" : "Next"}
              </button>
            </div>
          </div>
        </div>
      )}

      {panel === "chat" && !hidden && (
        <div className="mascot-bubble mascot-chat" role="dialog" aria-label="Chat with Axie">
          <div className="mascot-chat-header">
            <strong>Ask Axie</strong>
            <button
              type="button"
              className="mascot-chat-close"
              onClick={() => setPanel(null)}
              aria-label="Close chat"
              title="Close"
            >
              ×
            </button>
          </div>
          <div className="mascot-chat-scroll" ref={chatScrollRef}>
            {chatHistory.length === 0 && (
              <p className="mascot-chat-empty">
                Ask me about materials, the formula DSL, the experiment
                JSON, or what to try next.
              </p>
            )}
            {chatHistory.map((turn, i) => (
              <div key={i} className={`mascot-chat-turn role-${turn.role}`}>
                {turn.role === "assistant"
                  ? renderMarkdown(turn.content)
                  : turn.content}
              </div>
            ))}
            {chatBusy && <div className="mascot-chat-typing">Axie is thinking…</div>}
            {chatError && <div className="mascot-chat-error">{chatError}</div>}
          </div>
          <div className="mascot-chat-input-row">
            <textarea
              className="mascot-chat-input"
              rows={2}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={onChatKeyDown}
              placeholder="Ask Axie… (Enter to send, Shift+Enter for newline)"
              disabled={chatBusy}
            />
            <button
              type="button"
              className="mascot-btn"
              onClick={sendChat}
              disabled={chatBusy || chatInput.trim().length === 0}
            >
              Send
            </button>
          </div>
        </div>
      )}

      {sizeOpen && !hidden && (
        <div className="mascot-size-popover" role="dialog" aria-label="Resize mascot">
          <label className="mascot-size-label">
            Size
            <input
              type="range"
              min={MIN_SIZE}
              max={MAX_SIZE}
              step={5}
              value={size}
              onChange={(e) => setSize(Number(e.target.value))}
            />
            <span className="mascot-size-value">{size}px</span>
          </label>
        </div>
      )}

      <div className="mascot-toolbar" aria-label="Mascot controls">
        <button
          type="button"
          className="mascot-icon"
          onClick={() => {
            setStepIndex(0);
            setPanel((p) => (p === "tutorial" ? null : "tutorial"));
          }}
          aria-label="Replay tutorial"
          title="Replay tutorial"
          disabled={hidden}
        >
          ?
        </button>
        <button
          type="button"
          className="mascot-icon"
          onClick={() => setPanel((p) => (p === "chat" ? null : "chat"))}
          aria-label="Chat with Axie"
          title="Chat"
          disabled={hidden}
        >
          {/* Speech-bubble glyph (no emoji per project style). */}
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
            <path
              fill="currentColor"
              d="M3 2h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7l-3 3v-3H3a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"
            />
          </svg>
        </button>
        <button
          type="button"
          className={"mascot-icon" + (moveMode ? " is-on" : "")}
          onClick={() => setMoveMode((m) => !m)}
          onPointerDown={onMoveButtonPointerDown}
          onPointerMove={onMoveButtonPointerMove}
          onPointerUp={onMoveButtonPointerUp}
          onPointerCancel={onMoveButtonPointerUp}
          aria-label={moveMode ? "Lock position" : "Move mascot"}
          title={moveMode ? "Lock position (drag from here too)" : "Drag to move, or click to enable figure-drag"}
        >
          {/* Four-arrow move glyph. */}
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
            <path
              fill="currentColor"
              d="M8 1l3 3H9v3h3V5l3 3-3 3V9H9v3h2l-3 3-3-3h2V9H4v2L1 8l3-3v2h3V4H5l3-3z"
            />
          </svg>
        </button>
        <button
          type="button"
          className={"mascot-icon" + (sizeOpen ? " is-on" : "")}
          onClick={() => setSizeOpen((s) => !s)}
          aria-label="Resize mascot"
          title="Resize"
          disabled={hidden}
        >
          {/* Diagonal resize-handle glyph. */}
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
            <path
              fill="currentColor"
              d="M14 2v5h-2V5.4l-9.6 9.6H7v2H1v-6h2v2.6L12.6 4H10V2h4z"
            />
          </svg>
        </button>
        <button
          type="button"
          className={"mascot-icon" + (hidden ? " is-on" : "")}
          onClick={() => setHidden((h) => !h)}
          aria-label={hidden ? "Show mascot" : "Hide mascot"}
          title={hidden ? "Show Axie" : "Hide Axie"}
        >
          {/* Eye glyph — open when shown, struck-through when hidden. */}
          {hidden ? (
            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
              <path
                fill="currentColor"
                d="M2 2l12 12-1 1L11.5 13.5A8 8 0 0 1 8 14C4 14 1 8 1 8a14 14 0 0 1 2.5-3.3L1 2l1-1zM8 5a3 3 0 0 1 3 3c0 .5-.1 1-.3 1.4L7.6 6.3A3 3 0 0 1 8 5zm0-3a8 8 0 0 1 7 6c-.4.8-.9 1.7-1.6 2.5l-1.5-1.5A11 11 0 0 0 14 8s-2-4-6-4a7 7 0 0 0-2 .3L4.7 3a8 8 0 0 1 3.3-1z"
              />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
              <path
                fill="currentColor"
                d="M8 3C4 3 1 8 1 8s3 5 7 5 7-5 7-5-3-5-7-5zm0 8a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm0-2a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"
              />
            </svg>
          )}
        </button>
      </div>

      {!hidden && (
        <div
          className="mascot-figure"
          style={figureStyle}
          onPointerDown={onFigurePointerDown}
          onPointerMove={onFigurePointerMove}
          onPointerUp={onFigurePointerUp}
          onPointerCancel={onFigurePointerUp}
        >
          {assetMissing ? (
            <div
              className="mascot-fallback"
              style={figureStyle}
              title="Drop axolotl.glb in apps/web/public/models/ to enable the 3D mascot"
            >
              <div className="mascot-fallback-emoji" aria-hidden="true">
                ◑‿◐
              </div>
            </div>
          ) : (
            <MascotCanvas
              width={size}
              height={size}
              animation={animation}
              onLoaded={onLoaded}
              onError={onError}
            />
          )}
        </div>
      )}
    </div>
  );
}
